/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.22/esri/copyright.txt for details.
*/
import "../core/has.js";
import t from "../core/Logger.js";
import { acosClamped as r, cartesianToSpherical as n } from "../core/mathUtils.js";
import { isNone as s } from "../core/maybe.js";
import { i as e, e as o } from "./mat4.js";
import { g as i, b as a, a as c, i as u, l as f, c as m, m as p, f as g, p as l, n as h, s as j } from "./vec3.js";
import { c as y } from "./vec3f64.js";
import { c as d } from "./vec4.js";
import { c as M, f as A } from "./vec4f64.js";
import { closestPoint as v } from "../geometry/support/ray.js";
import { angle as b } from "../geometry/support/vector.js";
import { sv3d as R, sm4d as S } from "../geometry/support/vectorStacks.js";
var x = t.getLogger("esri.geometry.support.sphere");

function P() {
  return M();
}

function _(t) {
  var r = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : P();
  return d(r, t);
}

function q(t, r) {
  return A(t[0], t[1], t[2], r);
}

function w(t) {
  return t;
}

function C(t) {
  t[0] = t[1] = t[2] = t[3] = 0;
}

function k(t) {
  return t;
}

function E(t) {
  return Array.isArray(t) ? t[3] : t;
}

function L(t) {
  return Array.isArray(t) ? t : X;
}

function O(t, r, n, s) {
  return A(t, r, n, s);
}

function T(t, r, n) {
  return t !== n && i(n, t), n[3] = t[3] + r, n;
}

function z(t, r, n) {
  return x.error("sphere.setExtent is not yet supported"), t === n ? n : _(t, n);
}

function U(t, r, n) {
  if (s(r)) return !1;
  var e = r.origin,
      o = r.direction,
      i = V;
  i[0] = e[0] - t[0], i[1] = e[1] - t[1], i[2] = e[2] - t[2];
  var a = o[0] * o[0] + o[1] * o[1] + o[2] * o[2],
      c = 2 * (o[0] * i[0] + o[1] * i[1] + o[2] * i[2]),
      u = c * c - 4 * a * (i[0] * i[0] + i[1] * i[1] + i[2] * i[2] - t[3] * t[3]);
  if (u < 0) return !1;
  var f = Math.sqrt(u);
  var m = (-c - f) / (2 * a);
  var p = (-c + f) / (2 * a);
  return (m < 0 || p < m && p > 0) && (m = p), !(m < 0) && (n && (n[0] = e[0] + o[0] * m, n[1] = e[1] + o[1] * m, n[2] = e[2] + o[2] * m), !0);
}

var V = y();

function B(t, r) {
  return U(t, r, null);
}

function D(t, r, n) {
  if (U(t, r, n)) return n;
  var s = F(t, r, R.get());
  return a(n, r.origin, c(R.get(), r.direction, u(r.origin, s) / f(r.direction))), n;
}

function F(t, r, n) {
  var s = R.get(),
      i = S.get();
  m(s, r.origin, r.direction);
  var a = E(t);
  m(n, s, r.origin), c(n, n, 1 / f(n) * a);
  var u = J(t, r.origin),
      g = b(r.origin, n);
  return e(i), o(i, i, g + u, s), p(n, n, i), n;
}

function G(t, r, n) {
  return U(t, r, n) ? n : (v(r, L(t), n), H(t, n, n));
}

function H(t, r, n) {
  var s = g(R.get(), r, L(t)),
      e = c(R.get(), s, t[3] / f(s));
  return a(n, e, L(t));
}

function I(t, r) {
  var n = g(R.get(), r, L(t)),
      s = l(n),
      e = t[3] * t[3];
  return Math.sqrt(Math.abs(s - e));
}

function J(t, n) {
  var s = g(R.get(), n, L(t)),
      e = f(s),
      o = E(t),
      i = o + Math.abs(o - e);
  return r(o / i);
}

var K = y();

function N(t, r, s, e) {
  var o = g(K, r, L(t));

  switch (s) {
    case 0:
      {
        var _t = n(o, K)[2];
        return j(e, -Math.sin(_t), Math.cos(_t), 0);
      }

    case 1:
      {
        var _t2 = n(o, K),
            _r = _t2[1],
            _s = _t2[2],
            _i = Math.sin(_r);

        return j(e, -_i * Math.cos(_s), -_i * Math.sin(_s), Math.cos(_r));
      }

    case 2:
      return h(e, o);

    default:
      return;
  }
}

function Q(t, r) {
  var n = g(Y, r, L(t));
  return f(n) - t[3];
}

function W(t, r, n, s) {
  var e = Q(t, r),
      o = N(t, r, 2, Y),
      i = c(Y, o, n - e);
  return a(s, r, i);
}

var X = y(),
    Y = y(),
    Z = Object.freeze({
  __proto__: null,
  create: P,
  copy: _,
  fromCenterAndRadius: q,
  wrap: w,
  clear: C,
  fromRadius: k,
  getRadius: E,
  getCenter: L,
  fromValues: O,
  elevate: T,
  setExtent: z,
  intersectRay: U,
  intersectsRay: B,
  intersectRayClosestSilhouette: D,
  closestPointOnSilhouette: F,
  closestPoint: G,
  projectPoint: H,
  distanceToSilhouette: I,
  angleToSilhouette: J,
  axisAt: N,
  altitudeAt: Q,
  setAltitudeAt: W
});
export { C as a, _ as b, P as c, I as d, U as e, O as f, L as g, F as h, B as i, k as j, q as k, E as l, T as m, z as n, D as o, G as p, H as q, J as r, Z as s, N as t, Q as u, W as v, w };